import React from 'react';
import { Permission } from 'entities/user';
import { Role } from 'sections/user-management/sections/roles/data/roles-constants';
import { useRolesContext } from '../../../context/RolesProvider';
import { isRoleNameValid } from 'core/app/impl/isRoleNameValid';
import { isRoleDescriptionValid } from 'core/app/impl/isRoleDescriptionValid';

interface IState {
  originalRole: Role;
  roleName: string;
  roleDescription: string;
  rolePermissions: Permission[];
  roleUsers: string[];
  roleNameValid: boolean;
  roleDescriptionValid: boolean;
  rolePermissionsValid: boolean;
  setOriginalRole: (originalRole: Role) => void;
  setRoleName: (roleName: string) => void;
  setRoleDescription: (roleDescription: string) => void;
  setRolePermissions: (rolePermissions: Permission[]) => void;
  setRoleUsers: (roleUsers: string[]) => void;
  resetRoleChanges: () => void;
}

interface IEditRoleProviderProps {
  roleId: string;
}

const EditRoleContext = React.createContext<IState>(undefined);

export const EditRoleProvider: React.FC<IEditRoleProviderProps> = ({ roleId, children }) => {
  const { getRole } = useRolesContext();
  const [originalRole, setOriginalRole] = React.useState<Role>();
  const [roleName, setRoleName] = React.useState('');
  const [roleDescription, setRoleDescription] = React.useState('');
  const [rolePermissions, setRolePermissions] = React.useState<Permission[]>([]);
  const [roleUsers, setRoleUsers] = React.useState<string[]>([]);
  const [roleNameValid, setRoleNameValid] = React.useState(true);
  const [roleDescriptionValid, setRoleDescriptionValid] = React.useState(true);
  const [rolePermissionsValid, setRolePermissionsValid] = React.useState(true);

  const resetRoleChanges = () => {
    setRoleName(originalRole?.name ?? '');
    setRoleDescription(originalRole?.description ?? '');
    setRolePermissions(originalRole?.permissions ?? []);
    setRoleUsers(originalRole?.users ?? []);
    setRoleNameValid(true);
    setRoleDescriptionValid(true);
    setRolePermissionsValid(true);
  };

  React.useEffect(() => {
    const originalRole = getRole(roleId);

    setOriginalRole(originalRole);
    setRoleName(originalRole?.name ?? '');
    setRoleDescription(originalRole?.description ?? '');
    setRolePermissions(originalRole?.permissions ?? []);
    setRoleUsers(originalRole?.users ?? []);
  }, [getRole, roleId]);

  React.useEffect(() => {
    setRoleNameValid(isRoleNameValid(roleName));
  }, [roleName]);

  React.useEffect(() => {
    setRoleDescriptionValid(isRoleDescriptionValid(roleDescription));
  }, [roleDescription]);

  React.useEffect(() => {
    if (!rolePermissionsValid && rolePermissions.length > 0) {
      setRolePermissionsValid(true);
    } else if (rolePermissionsValid && rolePermissions.length === 0) {
      setRolePermissionsValid(false);
    }
  }, [rolePermissions, rolePermissionsValid]);

  return (
    <EditRoleContext.Provider
      value={{
        originalRole,
        roleName,
        roleDescription,
        rolePermissions,
        roleUsers,
        roleNameValid,
        roleDescriptionValid,
        rolePermissionsValid,
        setOriginalRole,
        setRoleName,
        setRoleDescription,
        setRolePermissions,
        setRoleUsers,
        resetRoleChanges,
      }}
    >
      {children}
    </EditRoleContext.Provider>
  );
};

export function useEditRole(): IState {
  const context = React.useContext(EditRoleContext);

  if (!context) {
    throw new Error('useEditRoleContext must be called from within a EditRoleProvider instance');
  }

  return context;
}

export const EditRoleConsumer = EditRoleContext.Consumer;
